import { useEffect, useState, useMemo, useCallback } from 'react';
import { orderAuditAPI } from '../../../../services';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import ExportButton from '../../../../components/ExportButton';
import './OrderAudit.css';

const EVENT_LABELS = {
  CREATED: 'Created',
  SENT_TO_VENDOR: 'Sent to LP',
  ACKNOWLEDGED: 'Acknowledged',
  PARTIALLY_FILLED: 'Partially Filled',
  FILLED: 'Filled',
  CANCELLED: 'Cancelled',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
  MODIFIED: 'Modified',
  TP_TRIGGERED: 'Take Profit',
  SL_TRIGGERED: 'Stop Loss',
  LIQUIDATED: 'Liquidated',
  ERROR: 'Error',
  REMOVED: 'Removed',
  CANCEL_REQUESTED: 'Cancel Requested'
};

const EVENT_COLORS = {
  CREATED: '#6366f1',
  SENT_TO_VENDOR: '#3b82f6',
  ACKNOWLEDGED: '#06b6d4',
  PARTIALLY_FILLED: '#f59e0b',
  FILLED: '#10b981',
  CANCELLED: '#ef4444',
  REJECTED: '#dc2626',
  EXPIRED: '#9ca3af',
  MODIFIED: '#8b5cf6',
  TP_TRIGGERED: '#10b981',
  SL_TRIGGERED: '#ef4444',
  LIQUIDATED: '#7c3aed',
  ERROR: '#f97316',
  REMOVED: '#6b7280',
  CANCEL_REQUESTED: '#eab308'
};

const INITIAL_FILTERS = {
  orderId: '',
  userId: '',
  eventType: '',
  startDate: '',
  endDate: ''
};

function OrderAudit() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [filters, setFilters] = useState({ ...INITIAL_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...INITIAL_FILTERS });

  const activeFilterCount = useMemo(() =>
    Object.values(appliedFilters).filter(v => v && v !== '').length,
    [appliedFilters]
  );

  // Detect unapplied filter changes
  const hasUnappliedChanges = useMemo(() => {
    const keys = Object.keys(INITIAL_FILTERS);
    return keys.some(k => (filters[k] || '') !== (appliedFilters[k] || ''));
  }, [filters, appliedFilters]);

  const fetchOrders = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const opts = { page, limit: 50 };
      if (appliedFilters.orderId) opts.orderId = appliedFilters.orderId.trim();
      if (appliedFilters.userId) opts.userId = appliedFilters.userId.trim();
      if (appliedFilters.eventType) opts.eventType = appliedFilters.eventType;
      if (appliedFilters.startDate) opts.startDate = appliedFilters.startDate;
      if (appliedFilters.endDate) opts.endDate = appliedFilters.endDate;

      const response = await orderAuditAPI.listOrders(opts);
      console.log('[OrderAudit] Response:', response);

      if (response && response.success) {
        const respData = response.data;
        let extracted = null;
        if (respData && respData.data && Array.isArray(respData.data)) {
          extracted = { data: respData.data, pagination: respData.pagination };
        } else if (Array.isArray(respData)) {
          extracted = { data: respData, pagination: response.pagination };
        } else if (respData && typeof respData === 'object') {
          if (Array.isArray(respData.data)) {
            extracted = { data: respData.data, pagination: respData.pagination };
          } else if (respData.data && respData.data.data && Array.isArray(respData.data.data)) {
            extracted = { data: respData.data.data, pagination: respData.data.pagination || respData.pagination };
          } else if (respData.data && Array.isArray(respData.data)) {
            extracted = { data: respData.data, pagination: respData.pagination };
          }
        }
        if (extracted) {
          console.log('[OrderAudit] Loaded', extracted.data.length, 'orders');
          setOrders(extracted.data);
          if (extracted.pagination) setPagination(extracted.pagination);
        } else {
          console.warn('[OrderAudit] Unexpected structure:', response);
          setOrders([]);
        }
      } else {
        setOrders([]);
        if (response && response.error) setError(response.error);
      }
    } catch (err) {
      console.error('[OrderAudit] Fetch error:', err);
      setError(err.message || 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => { fetchOrders(1); }, [fetchOrders]);

  const fetchTimeline = async (orderId) => {
    if (expandedOrder === orderId) {
      setExpandedOrder(null); setTimelineData(null); setTimelineError(null);
      return;
    }
    setExpandedOrder(orderId);
    setTimelineLoading(true);
    setTimelineError(null);
    try {
      const response = await orderAuditAPI.getTimeline(orderId);
      if (response && response.success) {
        let doc = response.data;
        if (doc && !doc.reports && doc.data && doc.data.reports) doc = doc.data;
        if (doc && !doc.reports && doc.data && doc.data.data && doc.data.data.reports) doc = doc.data.data;
        setTimelineData(doc);
      } else {
        setTimelineError('Failed to load timeline');
      }
    } catch (err) {
      setTimelineError(err.message || 'Failed');
    } finally {
      setTimelineLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('en-US', {
      month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    });
  };

  const getLatestEvent = (order) => {
    if (!order.latestReport) return null;
    const r = order.latestReport;
    let et = r.eventType;
    if (!et && r.report) {
      try {
        const p = typeof r.report === 'string' ? JSON.parse(r.report) : r.report;
        et = p.OrdStatus || p.eventType || p.type;
      } catch { /* ignore */ }
    }
    return {
      eventType: et,
      label: EVENT_LABELS[et] || et || 'Processing',
      color: EVENT_COLORS[et] || '#6b7280',
      timestamp: r.timestamp,
      message: r.message || ''
    };
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
  };

  const clearFilters = () => {
    setFilters({ ...INITIAL_FILTERS });
    setAppliedFilters({ ...INITIAL_FILTERS });
  };

  // Parse trade details from both firstReport (CREATED) and latestReport (vendor),
  // handling two different JSON key formats used across the audit trail:
  //   App format (CREATED/REMOVED):  { symbol, side, quantity, price, leverage }
  //   FIX format (vendor reports):  { Symbol, Side, OrderQty, LastPx/AvgPx }
  const FIX_SIDE_MAP = { '1': 'BUY', '2': 'SELL', BUY: 'BUY', SELL: 'SELL', buy: 'BUY', sell: 'SELL' };

  const parseDetailsFromReport = (report) => {
    if (!report?.report) return {};
    try {
      const d = typeof report.report === 'string' ? JSON.parse(report.report) : report.report;
      if (!d || typeof d !== 'object') return {};
      const rawSide = d.side ?? d.Side;
      const rawSideStr = String(rawSide ?? '');
      return {
        symbol: d.symbol || d.Symbol || '',
        side: FIX_SIDE_MAP[rawSideStr] || rawSideStr,
        quantity: d.quantity ?? d.OrderQty ?? d.orderQty ?? d.qty ?? '',
        price: d.price ?? d.LastPx ?? d.AvgPx ?? d.lastPx ?? '',
        leverage: d.leverage ?? d.Leverage ?? '',
      };
    } catch { return {}; }
  };

  const parseReportDetails = (order) => {
    const first = parseDetailsFromReport(order.firstReport);
    const last = parseDetailsFromReport(order.latestReport);

    // Prefer firstReport (usually CREATED with app keys), fall back to latestReport
    return {
      balanceChange: order.latestReport?.balanceChange,
      balanceCurrency: order.latestReport?.balanceCurrency,
      symbol: first.symbol || last.symbol || '',
      side: first.side || last.side || '',
      quantity: first.quantity ?? last.quantity ?? '',
      price: first.price ?? last.price ?? '',
      leverage: first.leverage ?? last.leverage ?? '',
    };
  };

  // ---- Export columns & data ----
  const auditExportData = useMemo(() => {
    return orders.map(order => {
      const latest = getLatestEvent(order);
      const details = parseReportDetails(order);
      return {
        orderId: order.orderId || order._id || '',
        userId: order.userId || '',
        status: latest?.label || '—',
        eventType: latest?.eventType || '',
        reportCount: order.reportCount || 0,
        message: latest?.message || '',
        symbol: details.symbol,
        side: details.side ? details.side.toUpperCase() : '',
        quantity: details.quantity,
        price: details.price,
        leverage: details.leverage,
        balanceChange: details.balanceChange,
        balanceCurrency: details.balanceCurrency,
        updatedAt: latest?.timestamp || order.updatedAt || '',
      };
    });
  }, [orders]);

  const auditExportColumns = [
    { key: 'orderId', header: 'Order ID' },
    { key: 'userId', header: 'User ID' },
    { key: 'status', header: 'Status' },
    { key: 'eventType', header: 'Event Type' },
    { key: 'reportCount', header: 'Events', align: 'right' },
    { key: 'message', header: 'Message' },
    { key: 'symbol', header: 'Symbol' },
    { key: 'side', header: 'Side' },
    { key: 'quantity', header: 'Quantity', format: (v) => v !== '' && v != null ? v : '—', align: 'right' },
    { key: 'price', header: 'Price', format: (v) => v != null ? v : '—', align: 'right' },
    { key: 'leverage', header: 'Leverage', format: (v) => v != null ? `${v}x` : '—', align: 'right' },
    { key: 'balanceChange', header: 'Balance Δ', format: (v, row) => v != null ? `${v.toFixed(2)} ${row?.balanceCurrency || 'USD'}` : '—', align: 'right' },
    { key: 'updatedAt', header: 'Last Updated', format: (v) => v ? formatDate(v) : '—' },
  ];

  // ── Render states ──

  if (loading && orders.length === 0) {
    return (
      <div className="audit-container">
        <div className="content-header">
          <h2><i className="fas fa-clipboard-list"></i> Order Audit</h2>
          <p>Full lifecycle audit trail for every order.</p>
        </div>
        <LoadingSpinner message="Loading orders..." size="medium" />
      </div>
    );
  }

  if (error && orders.length === 0) {
    return (
      <div className="audit-container">
        <div className="content-header">
          <h2><i className="fas fa-clipboard-list"></i> Order Audit</h2>
          <p>Full lifecycle audit trail for every order.</p>
        </div>
        <div className="audit-empty audit-error">
          <i className="fas fa-exclamation-triangle"></i>
          <h4>Failed to load orders</h4>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => fetchOrders(1)}>
            <i className="fas fa-redo"></i> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="audit-container">
      <div className="content-header">
        <h2><i className="fas fa-clipboard-list"></i> Order Audit</h2>
        <p>Full lifecycle audit trail for every order — from creation to fill, cancel, or rejection.</p>
      </div>

      {/* ══════ FILTER BAR ══════ */}
      <div className="audit-filter-bar">
        <div className="audit-filter-row">
          <div className="audit-filter-field">
            <label>Order ID</label>
            <input
              type="text"
              placeholder="e.g. 1000001"
              value={filters.orderId}
              onChange={e => handleFilterChange('orderId', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && applyFilters()}
            />
          </div>
          <div className="audit-filter-field">
            <label>User ID</label>
            <input
              type="text"
              placeholder="MongoDB user ID"
              value={filters.userId}
              onChange={e => handleFilterChange('userId', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && applyFilters()}
            />
          </div>
          <div className="audit-filter-field">
            <label>Status</label>
            <select
              value={filters.eventType}
              onChange={e => { handleFilterChange('eventType', e.target.value); applyFilters(); }}
            >
              <option value="">All Statuses</option>
              {Object.entries(EVENT_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div className="audit-filter-field">
            <label>From</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={e => { handleFilterChange('startDate', e.target.value); applyFilters(); }}
            />
          </div>
          <div className="audit-filter-field">
            <label>To</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={e => { handleFilterChange('endDate', e.target.value); applyFilters(); }}
            />
          </div>
          <div className="audit-filter-field audit-filter-actions">
            <ExportButton
              data={auditExportData}
              columns={auditExportColumns}
              filename="order-audit"
              title="Order Audit"
              subtitle={`${pagination.total || orders.length} orders • Exported on ${formatDate(new Date())}`}
              pdfOptions={{ orientation: 'landscape', fontSize: 7, padding: 2 }}
            />
            <button
              className={`audit-apply-btn ${hasUnappliedChanges ? 'pending' : ''}`}
              onClick={applyFilters}
              title="Apply filters (or press Enter in any field)"
            >
              <i className="fas fa-search"></i> Apply
            </button>
            {activeFilterCount > 0 && (
              <button className="audit-clear-btn" onClick={clearFilters}>
                <i className="fas fa-times"></i> Clear
              </button>
            )}
          </div>
        </div>
        {hasUnappliedChanges && (
          <div className="audit-filter-hint-row">
            <i className="fas fa-info-circle"></i>
            <span>Filters changed — click <strong>Apply</strong> or press <strong>Enter</strong> to update results</span>
          </div>
        )}
        {activeFilterCount > 0 && !hasUnappliedChanges && (
          <div className="audit-active-filters">
            <span className="audit-filter-hint">
              {activeFilterCount} active filter{activeFilterCount !== 1 ? 's' : ''}
              {' — '}{pagination.total || orders.length} result{(pagination.total || orders.length) !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* ══════ ORDER LIST ══════ */}
      <div className="audit-order-list">
        {orders.length === 0 ? (
          <div className="audit-empty">
            <i className="fas fa-inbox"></i>
            <h4>No orders found</h4>
            <p>{activeFilterCount > 0 ? 'Try adjusting your filters.' : 'Orders will appear here once trading begins.'}</p>
            {activeFilterCount > 0 && (
              <button className="retry-btn" onClick={clearFilters}>
                <i className="fas fa-times"></i> Clear Filters
              </button>
            )}
          </div>
        ) : (
          orders.map((order) => {
            const latest = getLatestEvent(order);
            const oid = order.orderId || order._id;
            const isExpanded = expandedOrder === oid;
            return (
              <div key={oid} className={`audit-card ${isExpanded ? 'expanded' : ''}`}>
                <div className="audit-card-main" onClick={() => fetchTimeline(oid)}>
                  <div className="audit-card-left">
                    <span className="audit-order-id">#{oid}</span>
                    {latest && (
                      <span className="audit-status" style={{ background: latest.color }}>
                        {latest.label}
                      </span>
                    )}
                  </div>
                  <div className="audit-card-center">
                    <span className="audit-user">{order.userId || '-'}</span>
                    <span className="audit-count">{order.reportCount || 0} events</span>
                  </div>
                  <div className="audit-card-right">
                    <span className="audit-time">{formatDate(latest?.timestamp || order.updatedAt)}</span>
                    <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                  </div>
                </div>

                {isExpanded && (
                  <div className="audit-timeline">
                    {timelineLoading ? (
                      <LoadingSpinner message="Loading timeline..." size="small" />
                    ) : timelineError ? (
                      <div className="timeline-empty" style={{ color: 'var(--admin-danger)' }}>
                        {timelineError}
                        <button className="retry-btn" onClick={() => fetchTimeline(oid)}>Retry</button>
                      </div>
                    ) : timelineData && timelineData.reports ? (
                      <div className="timeline-list">
                        {[...timelineData.reports].reverse().map((entry, idx) => {
                          const eType = entry.eventType;
                          const color = EVENT_COLORS[eType] || '#6b7280';
                          const label = EVENT_LABELS[eType] || eType || 'Event';
                          let details = null;
                          try { details = typeof entry.report === 'string' ? JSON.parse(entry.report) : entry.report; } catch { /* ignore */ }
                          return (
                            <div key={idx} className="timeline-entry">
                              <div className="timeline-dot" style={{ background: color }}></div>
                              {idx < timelineData.reports.length - 1 && <div className="timeline-line"></div>}
                              <div className="timeline-content">
                                <div className="timeline-header">
                                  <span className="timeline-event" style={{ color }}>{label}</span>
                                  <span className="timeline-time">{formatDate(entry.timestamp)}</span>
                                </div>
                                <div className="timeline-message">{entry.message || '-'}</div>
                                {entry.balanceChange !== 0 && (
                                  <div className={`timeline-balance ${entry.balanceChange > 0 ? 'positive' : 'negative'}`}>
                                    Balance: {entry.balanceChange > 0 ? '+' : ''}{entry.balanceChange.toFixed(2)} {entry.balanceCurrency || 'USD'}
                                  </div>
                                )}
                                {details && details.symbol && (
                                  <div className="timeline-meta">
                                    {details.symbol} {details.side?.toUpperCase()} {details.quantity} @ {details.price}
                                    {details.leverage && ` · ${details.leverage}x`}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="timeline-empty">No timeline data available.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* ══════ PAGINATION ══════ */}
      {pagination.pages > 1 && (
        <div className="audit-pagination">
          <button onClick={() => fetchOrders(pagination.page - 1)} disabled={pagination.page <= 1}>
            <i className="fas fa-chevron-left"></i>
          </button>
          <span>
            Page {pagination.page} of {pagination.pages}
            {pagination.total ? ` · ${pagination.total} total` : ''}
          </span>
          <button onClick={() => fetchOrders(pagination.page + 1)} disabled={pagination.page >= pagination.pages}>
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}
    </div>
  );
}

export default OrderAudit;
